import { FC, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import Handlebars from 'handlebars';
import { Block } from 'apps/ng/src/app/services';
import { BlockControls } from './BlockControls';
import { listActionTypes } from '../../../../../../../../../constants';

interface ColumnRendererProps {
  columnName: string;
  columnPath: string;
  items: (Block & { instanceId: string })[];
  isSelected: boolean;
  onSelect: () => void;
  onBlockAction?: (action: any) => void;
  disabledActions?: string[];
  zoomLevel?: number;
  viewportWidth?: number;
}

const columnBlockItemStyles = `
.__clatter-preview__column-block-item {
  position: relative;
  margin-bottom: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  background: white;
  transition: outline 0.3s ease-in-out;
}

.__clatter-preview__column-block-item:hover {
  outline: 2px solid blue;
}

/* Only show controls when hovering THIS column block, not nested elements */
.__clatter-preview__column-block-item:hover .block-controls-header {
  display: flex;
  z-index: 10;
}

.__clatter-preview__column-block-preview {
  padding: 8px;
  min-height: 80px;
}
`;

const ColumnRenderer: FC<ColumnRendererProps> = ({
  columnName: _columnName,
  columnPath,
  items,
  isSelected,
  onSelect,
  onBlockAction,
  disabledActions = [],
  zoomLevel = 100,
  viewportWidth,
}) => {
  return (
    <>
      <style>{columnBlockItemStyles}</style>
      <div
        className={`__clatter-preview__column-slot ${isSelected ? '__clatter-preview__column-selected' : ''}`}
        data-column-path={columnPath}
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
      >
        {items.length === 0 ? (
          <div className="__clatter-preview__column-empty">
            <span>
              {isSelected ? 'Selected - Click a block from the list to add it here' : 'Click to select this column'}
            </span>
          </div>
        ) : (
          items.map((item, index) => (
            <div key={item.instanceId || index} className="__clatter-preview__column-block-item">
              <div
                className="__clatter-preview__column-block-preview"
                dangerouslySetInnerHTML={{ __html: item.html || `<p>${item.displayName || item.name}</p>` }}
              />
              <BlockControls
                title={item.displayName || item.name}
                disabledActions={[
                  ...(index === 0 ? ['MOVE_UP'] : []),
                  ...(index === items.length - 1 ? ['MOVE_DOWN'] : []),
                  ...(disabledActions.includes('OPEN_SETTINGS') ? ['OPEN_SETTINGS'] : []),
                ]}
                onMoveUp={() => {
                  onBlockAction?.({
                    type: 'MOVE_UP_IN_COLUMN',
                    payload: { block: item, columnPath, index },
                  });
                }}
                onMoveDown={() => {
                  onBlockAction?.({
                    type: 'MOVE_DOWN_IN_COLUMN',
                    payload: { block: item, columnPath, index },
                  });
                }}
                onRemove={() => {
                  onBlockAction?.({
                    type: 'REMOVE_FROM_COLUMN',
                    payload: { block: item, columnPath, index },
                  });
                }}
                onSettings={() => {
                  onBlockAction?.({
                    type: 'OPEN_SETTINGS',
                    payload: item,
                  });
                }}
                zoomLevel={zoomLevel}
                viewportWidth={viewportWidth}
              />
            </div>
          ))
        )}
      </div>
    </>
  );
};

interface SectionWithColumnsProps {
  block: Block & {
    instanceId: string;
    columns?: Record<string, (Block & { instanceId: string })[]>;
  };
  context: Record<string, any>;
  selectedColumn?: string | null;
  onColumnSelect?: (columnPath: string) => void;
  onAction?: (action: any) => void;
  disabledActions: string[];
  zoomLevel?: number;
  viewportWidth?: number;
}

const sectionStyles = `
.__clatter-preview__section-with-columns {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: auto;
  margin-bottom: 16px;
  transition: outline 0.3s ease-in-out, z-index 0.3s ease-in-out;
}

.__clatter-preview__section-with-columns:hover {
  z-index: 3;
  outline: 2px solid blue;
}

/* Only show section controls when hovering the section itself, not when hovering nested column blocks */
.__clatter-preview__section-with-columns:hover:not(:has(.__clatter-preview__column-block-item:hover)) .block-controls-header {
  cursor: pointer;
  display: flex;
  z-index: 10;
}

.__clatter-preview__section-body {
  padding: 16px;
  position: relative;
}
`;

export const SectionWithColumns: FC<SectionWithColumnsProps> = ({
  block,
  context,
  selectedColumn,
  onColumnSelect,
  onAction,
  disabledActions,
  zoomLevel = 100,
  viewportWidth,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const rootsRef = useRef<Map<string, any>>(new Map());

  useEffect(() => {
    const container = sectionRef.current;
    if (!container) return;

    let compiledHtml = '';
    try {
      const template = Handlebars.compile(block.html || '');
      compiledHtml = template(context, {
        data: {
          System: {
            BasePath: `/`,
            Pages: [],
            Page: {
              DisplayName: 'Preview',
              name: 'preview',
            },
            Sections: [{ DisplayName: 'Section #1' }, { DisplayName: 'Section #2' }, { DisplayName: 'Section #3' }],
            CurrentYear: new Date().getFullYear(),
          },
        },
      });

      container.innerHTML = compiledHtml;
    } catch (error) {
      console.error('SectionWithColumns: Error compiling template:', error);
      console.error('Block details:', {
        name: block.name,
        instanceId: block.instanceId,
        html: block.html?.substring(0, 100) + '...',
        context: Object.keys(context || {}),
      });
      container.innerHTML = `<div style="color: red; padding: 20px;">Error compiling template: ${error}</div>`;
      return;
    }

    const columnElements = container.querySelectorAll('[data-column]');

    if (columnElements.length === 0) {
      console.warn('SectionWithColumns: No column elements found!');
      return;
    }

    // Clean up old roots asynchronously to avoid race condition
    const oldRoots = Array.from(rootsRef.current.values());
    if (oldRoots.length > 0) {
      setTimeout(() => {
        oldRoots.forEach((root) => {
          try {
            root.unmount();
          } catch (e) {
            // Ignore unmount errors
          }
        });
      }, 0);
    }
    rootsRef.current.clear();

    columnElements.forEach((el) => {
      const columnName = el.getAttribute('data-column');
      if (!columnName) return;

      const columnPath = `${block.instanceId}.${columnName}`;
      const mountPoint = document.createElement('div');
      el.innerHTML = '';
      el.appendChild(mountPoint);

      const root = createRoot(mountPoint);
      rootsRef.current.set(columnPath, root);

      const columnBlocks = block.columns?.[columnName] || [];

      root.render(
        <ColumnRenderer
          columnName={columnName}
          columnPath={columnPath}
          items={columnBlocks}
          isSelected={selectedColumn === columnPath}
          onSelect={() => {
            onColumnSelect?.(columnPath);
          }}
          onBlockAction={onAction}
          disabledActions={disabledActions}
          zoomLevel={zoomLevel}
          viewportWidth={viewportWidth}
        />,
      );
    });

    return () => {
      // Cleanup on unmount - also async to avoid race conditions
      const currentRoots = Array.from(rootsRef.current.values());
      setTimeout(() => {
        currentRoots.forEach((root) => {
          try {
            root.unmount();
          } catch (e) {
            // Ignore unmount errors
          }
        });
      }, 0);
      rootsRef.current.clear();
    };
  }, [block, context, selectedColumn, onColumnSelect, onAction, disabledActions]);

  return (
    <>
      <style>{sectionStyles}</style>
      <div className="__clatter-preview__section-with-columns">
        <div className="__clatter-preview__section-body" ref={sectionRef}>
          {/* Content will be rendered here */}
        </div>

        <BlockControls
          title={block.displayName || block.name}
          disabledActions={disabledActions}
          onMoveUp={() => onAction?.({ type: listActionTypes.MOVE_UP, payload: block })}
          onMoveDown={() => onAction?.({ type: listActionTypes.MOVE_DOWN, payload: block })}
          onRemove={() => onAction?.({ type: listActionTypes.REMOVE, payload: block })}
          onSettings={() => onAction?.({ type: listActionTypes.OPEN_SETTINGS, payload: block })}
          zoomLevel={zoomLevel}
          viewportWidth={viewportWidth}
        />
      </div>
    </>
  );
};
